import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  fetchInvoices,
  type InvoiceFilterInput,
  type InvoicesResponse,
  type ReleaseStatus,
} from '@/integrations/faturamento/client';
import { excludeCutoffItems } from '@/config/legacyResicleanCutoff';
import { invoiceScopeFilter } from '@/hooks/invoiceScopeFilter';

function toYYYYMMDD(d: Date): string {
  return d.toISOString().split('T')[0];
}

interface UseInvoicesParams {
  page?: number;
  pageSize?: number;
  dueDateRange?: { start: Date | null; end: Date | null } | null;
  /** Competências específicas (`YYYY-MM`), não um intervalo — ver GR-670. */
  billingPeriods?: string[];
  /** Geradores escolhidos (ids internos). A API expande a subárvore; o front não lista filiais. */
  generatorIds?: number[];
  /** Unidades escolhidas à mão (ids exatos do gerador pagador). */
  establishmentIds?: number[];
  cnpj?: string;
  status?: string;
  /**
   * Inclui as medições em análise interna, que ainda não foram faturadas. O gateway assume `false`
   * quando o campo não vai — é disso que dependem a exportação CSV e a sonda de estado vazio da
   * tela, que continuam contando apenas faturas.
   */
  includeInAnalysis?: boolean;
  /** Estado do ciclo — complementa `status`, que é o status da cobrança na Omie. */
  releaseStatus?: ReleaseStatus[];
  /** `'FATURA'` e/ou `'MEDICAO'`. Os dois juntos (ou vazio) têm o mesmo efeito de ausente. */
  types?: string[];
}

export const useInvoices = ({
  page = 1,
  pageSize = 10,
  dueDateRange,
  billingPeriods,
  generatorIds,
  establishmentIds,
  cnpj,
  status,
  includeInAnalysis,
  releaseStatus,
  types,
}: UseInvoicesParams = {}) => {
  const filter: InvoiceFilterInput = {
    page,
    page_size: pageSize,
    due_date_from: dueDateRange?.start ? toYYYYMMDD(dueDateRange.start) : undefined,
    due_date_to: dueDateRange?.end ? toYYYYMMDD(dueDateRange.end) : undefined,
    billing_periods: billingPeriods?.length ? billingPeriods : undefined,
    ...invoiceScopeFilter(generatorIds, establishmentIds),
    cnpj: cnpj?.trim() || undefined,
    status: status && status !== 'all' ? status : undefined,
    include_in_analysis: includeInAnalysis || undefined,
    release_status: releaseStatus?.length ? releaseStatus : undefined,
    types: types?.length ? types : undefined,
  };

  return useQuery({
    queryKey: ['invoices', filter],
    queryFn: async (): Promise<InvoicesResponse> => {
      const result = await fetchInvoices(filter);
      // MC-107: esconde a fatura inteira dos geradores da carteira Resiclean
      // cuja competência cai no período com dados retroativos fictícios
      // (jan-jul/2026) — independente de o usuário ter filtrado por
      // competência ou não (sem filtro, o backend devolve tudo).
      const data = excludeCutoffItems(
        result.data,
        (invoice) => invoice.paying_generator?.id,
        (invoice) => invoice.billing_period,
        'month',
      );
      const removedCount = result.data.length - data.length;
      return {
        ...result,
        data,
        pagination: {
          ...result.pagination,
          count: Math.max(0, result.pagination.count - removedCount),
        },
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
  });
};
